import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import qs from 'query-string';

import MarketingMaterialConfigurator from '../MarketingMaterialConfigurator';
import useIsNativeWebView from '../../../../../../../services/native-bridge/hooks/useIsWebView';
import useAlbumFolderMarketingAssets from '../../../../../views/AlbumFolder/AlbumFolderMarketing/components/AlbumFolderMarketingList/useAlbumFolderMarketingAssets';
import {
  postNativeAppMarketingMaterialConfigurationComplete,
  postNativeAppMarketingMaterialConfigurationFailure,
  postNativeAppMarketingMaterialConfigurationUpsellRequired,
} from '../../../../../../../services/native-bridge/native-apps-interface';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import FullScreenLoader from '../../../../../../../components/global/FullScreenLoader';
import { getAsset } from '../../../../../../../util/assets';
import WaldoficationSimplifiedUpsellModal from '../../../../../../../components/global/WaldoficationSimplifiedUpsell/modal';
import ErrorView from '../../../../../../../components/global/ErrorView';

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumFolderId: PropTypes.string,
    }),
  }),
};

const PublicMarketingMaterialConfigurator = ({ match, location }) => {
  const isNativeWebView = useIsNativeWebView();
  const { albumFolderId } = match.params;
  const { marketingMaterialType: marketingMaterialTypeNonParsed } = qs.parse(
    location.search,
  );

  const marketingMaterialType = (
    marketingMaterialTypeNonParsed || ''
  ).toUpperCase();

  const [marketingMaterialsCreated, setMarketingMaterialsCreated] =
    useState(false);
  const [waldoficationUpsellOpen, setWaldoficationUpsellOpen] = useState(false);

  const openAlert = useOpenAlert();

  // marketingMaterialType
  const {
    marketingMaterials,
    onMarketingMaterialsAssetGenerate,
    assetsCreating,
    availableMarketingMaterialTypes,
    albumFolder,
  } = useAlbumFolderMarketingAssets({
    albumFolderId,
  });

  useEffect(() => {
    if (!marketingMaterialType) {
      if (isNativeWebView) {
        postNativeAppMarketingMaterialConfigurationFailure(
          new Error('Marketing material type was not provided'),
        );
      } else {
        openAlert({
          type: 'error',
          title: 'It looks like an error happened',
          message: 'Marketing material type was not provided',
        });
      }
    }
  }, [marketingMaterialType]);

  const marketingMaterialMetaItem = availableMarketingMaterialTypes.find(
    marketingMaterialMeta =>
      marketingMaterialMeta.type === marketingMaterialType,
  );
  const marketingMaterial = marketingMaterials.find(
    marketingMaterialRecord =>
      marketingMaterialRecord.type === marketingMaterialType,
  );

  const onMarketingMaterialsCreatedSuccess = () => {
    if (isNativeWebView) {
      postNativeAppMarketingMaterialConfigurationComplete({
        albumFolderId,
        albumFolderName: albumFolder?.name,
        albumSubtype: albumFolder?.album_subtype,
      });
    } else {
      openAlert({
        type: 'success',
        title: 'Update successful',
        message: 'Marketing material has been re-generated!',
      });
    }
  };

  useEffect(() => {
    if (
      assetsCreating?.length === 0 &&
      marketingMaterialsCreated &&
      marketingMaterial
    ) {
      onMarketingMaterialsCreatedSuccess();
    }
  }, [assetsCreating, marketingMaterials, marketingMaterialsCreated]);

  const onConfigurationComplete = async ({ configuration }) => {
    await onMarketingMaterialsAssetGenerate({
      type: marketingMaterialType,
      configuration,
    });

    setMarketingMaterialsCreated(true);
  };

  const onUpsellRequired = () => {
    if (isNativeWebView) {
      postNativeAppMarketingMaterialConfigurationUpsellRequired();
    } else {
      setWaldoficationUpsellOpen(true);
    }
  };

  if (assetsCreating?.length > 0) {
    return (
      <div className="full-screen-loader flex-column">
        <img
          className="full-screen-loading-icon"
          src={getAsset('/waldo-icon.svg')}
          alt=""
        />
        <p className="my-2 text-dark text-center">
          Please wait. Your marketing material is being generated&hellip;
        </p>
      </div>
    );
  }

  if (!albumFolder || !marketingMaterialType) {
    return <FullScreenLoader />;
  }

  if (!marketingMaterialMetaItem) {
    return (
      <ErrorView
        location={{
          state: {
            error: 'Your marketing material type is not supported.',
          },
        }}
      />
    );
  }

  return (
    <Fragment>
      <MarketingMaterialConfigurator
        defaultConfiguration={marketingMaterial?.configuration}
        onConfigurationComplete={onConfigurationComplete}
        marketingMaterialType={marketingMaterialType}
        onUpsellRequired={onUpsellRequired}
        albumFolder={albumFolder}
        label={marketingMaterialMetaItem.label}
      />
      <WaldoficationSimplifiedUpsellModal
        forceOpen={waldoficationUpsellOpen}
        actionProps={{
          albumFolderId: albumFolder.id,
          featureId: 'white_label_marketing_prints_enabled',
          onCheckoutSuccess: () => {
            setWaldoficationUpsellOpen(false);
          },
        }}
        onClose={() => {
          setWaldoficationUpsellOpen(false);
        }}
      />
    </Fragment>
  );
};

PublicMarketingMaterialConfigurator.propTypes = propTypes;

export default compose(withRouter)(PublicMarketingMaterialConfigurator);
